import { useCallback, useRef, useState } from 'preact/hooks';
import './ProductMiniatureButtons.less';
import Popover from '../../popover/Popover';
import ActionList, { type ActionListItem } from '../../list/ActionList';
import ProductAnchorButton from '../ProductAnchorButton';
import { useTranslation } from '../../../hooks/useTranslation';
import useAppDispatch from '../../../hooks/useAppDispatch';
import { dispatchEvent } from '../../../services/EventService';
import type { Product } from '../../../models/Product';
import useAppSelector from '../../../hooks/useAppSelector';

export type ProductMiniatureButtonsAction =
    | 'compare'
    | 'product-search-similar'
    | 'product-search-combinations'
    | 'product-mix-and-match'
    | 'product-detail'
    | 'add-to-cart';

export type ProductMiniatureButtonsProps = {
    product: Product;
    visibleActions?: ProductMiniatureButtonsAction[];
};

const ProductMiniatureButtons = ({
    product,
    visibleActions = []
}: ProductMiniatureButtonsProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const selectedForComparisonProductIds = useAppSelector(
        state => state.productComparisonState.selectedProductIds
    );
    const [isOpen, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const onAnchorClick = useCallback(
        (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            setOpen(!isOpen);
        },
        [isOpen]
    );

    const onAction = useCallback(
        (action: ProductMiniatureButtonsAction) => {
            dispatchEvent({
                type: 'bri.click_product_card_action',
                isInteraction: true,
                source: 'brilliant',
                brilliantSource: 'ui',
                action,
                productId: product.productId
            });
            switch (action) {
                case 'compare':
                    return dispatch.productComparisonState.toggleProductSelection(
                        product.productId
                    );
                case 'product-search-similar':
                    return dispatch.featureState.startFeature({
                        featureType: 'product-search-similar',
                        similarToProductIds: [product.productId]
                    });
                case 'product-search-combinations':
                    return dispatch.featureState.startFeature({
                        featureType: 'product-search-combinations',
                        combinationWithProductIds: [product.productId]
                    });
                case 'product-mix-and-match':
                    return dispatch.featureState.startFeature({
                        featureType: 'product-mix-and-match',
                        productId: product.productId
                    });
                case 'product-detail':
                case 'add-to-cart':
                    return dispatch.featureState.startFeature({
                        featureType: 'product-detail',
                        productId: product.productId,
                        source: 'brilliant'
                    });
            }
        },
        [dispatch, product.productId, product]
    );

    const onActionClick = useCallback(
        (event: MouseEvent, action: ProductMiniatureButtonsAction) => {
            event.stopPropagation();
            event.preventDefault();
            setOpen(false);
            void onAction(action);
        },
        [onAction]
    );

    const isComparing = selectedForComparisonProductIds.includes(
        product.productId
    );

    const allActions: ActionListItem<ProductMiniatureButtonsAction>[] = [
        {
            id: 'add-to-cart',
            label: t('ADD_TO_CART_BUTTON_LABEL_ONE'),
            iconId: 'cart-plus'
        },
        {
            id: 'product-search-similar',
            label: t('PRODUCT_CARD_SIMILAR_BUTTON_LABEL'),
            iconId: 'similar'
        },
        {
            id: 'compare',
            label: t(
                isComparing
                    ? 'PRODUCT_CARD_REMOVE_COMPARISON_BUTTON_LABEL'
                    : 'PRODUCT_CARD_COMPARISON_BUTTON_LABEL'
            ),
            iconId: isComparing ? 'close' : 'compare'
        },
        {
            id: 'product-search-combinations',
            label: t('PRODUCT_CARD_COMBINATIONS_BUTTON_LABEL'),
            iconId: 'join'
        },
        {
            id: 'product-mix-and-match',
            label: t('PRODUCT_CARD_MIX_AND_MATCH_BUTTON_LABEL'),
            iconId: 'outfit'
        }
    ];
    const actions = allActions.filter(item =>
        visibleActions?.includes(item.id)
    );
    if (actions.length === 0) {
        return null;
    }
    return (
        <div className={`bri-ProductMiniatureButtons`}>
            <ProductAnchorButton
                selected={isOpen}
                onClick={onAnchorClick}
                marginRight="0"
                marginTop="0"
                ref={anchorRef}
            />
            <Popover
                isOpen={isOpen}
                anchorRef={anchorRef}
                onClose={() => setOpen(false)}
            >
                <ActionList
                    items={actions}
                    onClick={(event, item) => onActionClick(event, item.id)}
                />
            </Popover>
        </div>
    );
};

export default ProductMiniatureButtons;
